import React, { useEffect, useRef, useState } from 'react';
import { ClipboardList, Loader2, Calculator, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { C, PanelShell, Collapsible } from './shared';
import { ModuleBlock } from './ModuleBlock';
import { QuoteResult } from './QuoteOptions';

const ZONES = { storm_zone: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'], theft_zone: ['T1', 'T2', 'T3', 'T4', 'T5'], eq_zone: ['E1', 'E2', 'E3', 'E4'],
  flood_band: ['F0', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6'], bal: ['OUTSIDE_BPA', 'BAL_LOW_IN_BPA', 'BAL_12_5', 'BAL_19', 'BAL_29', 'BAL_40', 'BAL_FZ'] };
export { ZONES };

export const ResidentialQuotePanel = ({ api, pid }) => {
  const [products, setProducts] = useState(null);
  const [product, setProduct] = useState('ra-home-v1');
  const [rendered, setRendered] = useState(null);
  const [answers, setAnswers] = useState({});
  const [confirmed, setConfirmed] = useState({});
  const [assumptions, setAssumptions] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [rec, setRec] = useState(null);
  const [pending, setPending] = useState(false);
  const timer = useRef(null);

  useEffect(() => { api.get('/quote/residential/products').then(({ data }) => setProducts(data)).catch(e => console.error('API error:', e?.message)); }, [api]);

  useEffect(() => {
    setAnswers({}); setConfirmed({}); setRec(null); setErr(null); setRendered(null);
    if (!pid) return;
    api.get('/quote/residential/questions', { params: { product, pid } }).then(({ data }) => setRendered(data))
      .catch(e => { console.error('API error:', e?.message); setErr(e?.response?.data?.detail || e?.message); });
  }, [api, pid, product]);

  const rerender = (a, c) => {
    clearTimeout(timer.current);
    setPending(true);
    timer.current = setTimeout(() => {
      api.post('/quote/residential/questions', { product, pid, answers: a, confirmed: Object.keys(c).filter(k => c[k]) })
        .then(({ data }) => setRendered(data)).catch(e => console.error('API error:', e?.message)).finally(() => setPending(false));
    }, 450);
  };
  const setAnswer = (qid, v) => { const a = { ...answers, [qid]: v }; setAnswers(a); rerender(a, confirmed); };
  const confirm = qid => { const c = { ...confirmed, [qid]: true }; setConfirmed(c); rerender(answers, c); };
  const change = qid => { const c = { ...confirmed, [qid]: false }; setConfirmed(c); };

  const price = async () => {
    setBusy(true); setErr(null); setRec(null);
    try {
      const body = { product, pid, answers, confirmed: Object.keys(confirmed).filter(k => confirmed[k]), assumptions };
      const { data } = await api.post('/quote/residential', body);
      setRec(data);
    } catch (e) {
      console.error('API error:', e?.message);
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? `${d.message} (${(d.missing || []).join(', ')})` : d || e?.message);
    }
    setBusy(false);
  };

  const ko = rendered?.knockouts_fired?.find(k => k.stop);
  return (
    <PanelShell title="Residential quote — question engine + options" icon={<ClipboardList className="w-3 h-3" />} testid="rq-panel"
      meta={rendered ? <span>question set {rendered.question_set.version} · {rendered.question_set.enabled} of {rendered.question_set.total} enabled · <span className="font-mono">{rendered.question_set.hash}</span></span> : 'M3'}>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <select value={product} onChange={e => setProduct(e.target.value)} className="h-8 rounded-sm border border-input bg-white px-2 text-xs" data-testid="rq-product-select">
          {(products?.products || [{ id: 'ra-home-v1', label: 'Rural Affinity Home' }, { id: 'ra-landlord-v1', label: 'Rural Affinity Landlord' }]).map(p => <option key={p.id} value={p.id}>{p.id} — {p.label}</option>)}
        </select>
        {rendered && <span className="text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1" style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }} data-testid="rq-typed-so-far">{pending && <Loader2 className="w-3 h-3 animate-spin" data-testid="rq-rerender-pending" />}typed so far: {rendered.typed_inputs} · confirmed {rendered.confirmed_fields.length} · defaults {rendered.defaulted_fields.length} · ≤ {rendered.budget.typed_inputs_max} on {rendered.mode}</span>}
        {rendered?.facts && <span className="text-[10px] px-2 py-1 rounded" style={{ background: C.bg, color: C.slate }} data-testid="rq-facts">known after the address: {Object.entries(rendered.facts).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>}
      </div>
      {!pid && <p className="mt-2 text-[11px]" style={{ color: C.slate }}>resolve an address first — the questions prefill from its profile and hazards.</p>}
      {err && <p className="mt-2 text-[11px]" style={{ color: C.red }} data-testid="rq-error">{String(err)}</p>}
      {ko && (
        <p className="mt-2 text-[11px] font-semibold flex items-center gap-1.5 rounded-sm p-2" style={{ color: ko.action === 'decline' ? C.red : C.amber, background: 'rgba(226,75,74,0.05)' }} data-testid="rq-stop-banner">
          <Ban className="w-3.5 h-3.5" /> {ko.action} at “{ko.stage}” — {ko.reason}. Clears when {ko.clears_when}.
        </p>
      )}
      {rendered && !rendered.modules.length && <p className="mt-2 text-[11px]" style={{ color: C.slate }}>loading…</p>}
      {rendered?.modules.map(m => <ModuleBlock key={m.name} m={m} answers={answers} confirmed={confirmed} setAnswer={setAnswer} confirm={confirm} change={change} />)}
      {rendered && (
        <Collapsible label="underwriter assumptions (zones no free layer publishes)" count={Object.keys(assumptions).length || ''} testid="rq-assumptions-toggle">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-1">
            {Object.entries(ZONES).map(([k, vals]) => (
              <label key={k} className="text-[10px] flex flex-col gap-0.5" style={{ color: C.slate }}>{k.replace('_', ' ')}
                <select value={assumptions[k] || ''} onChange={e => setAssumptions(a => { const n = { ...a }; if (e.target.value) n[k] = e.target.value; else delete n[k]; return n; })} className="h-7 rounded-sm border border-input bg-white px-1 text-xs" data-testid={`rq-assumption-${k}`}>
                  <option value="">from hazards / assumed</option>
                  {vals.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
            ))}
          </div>
        </Collapsible>
      )}
      {rendered && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <Button onClick={price} disabled={busy || !!ko} className="rounded-sm h-8" style={{ background: ko ? C.slate : C.blue }} data-testid="rq-price-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}<span className="ml-1.5 text-xs">{ko ? 'journey stopped' : 'Price 3 options'}</span>
          </Button>
          {rendered.missing_required.length > 0 && <span className="text-[10px]" style={{ color: C.amber }} data-testid="rq-missing-required">required: {rendered.missing_required.join(', ')}</span>}
        </div>
      )}
      <QuoteResult rec={rec} />
    </PanelShell>
  );
};
